'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import SafeImage from '@ui/SafeImage';
import { logEvent } from '@util/analytics';
import useLogWhenInView from '@util/hooks/useLogWhenInView';
import Link from 'next/link';
import { useRef } from 'react';

const PopularRidingGearCategories = () => {
  const ref = useLogWhenInView(() => {
    logEvent('view_item_list', {
      item_list_id: 'popular_riding_gear_categories',
      item_list_name: 'Popular Riding Gear Categories',
      item_category: 'popular_riding_gear_categories',
      items: [
        {
          item_id: 'boots',
          item_name: 'Boots',
          item_category: 'popular_riding_gear_categories',
        },
        {
          item_id: 'helmets',
          item_name: 'Helmets',
          item_category: 'popular_riding_gear_categories',
        },
        {
          item_id: 'gear',
          item_name: 'Gear',
          item_category: 'popular_riding_gear_categories',
        },
        {
          item_id: 'protective',
          item_name: 'Protective',
          item_category: 'popular_riding_gear_categories',
        },
      ],
    });
  });

  const categories = [
    {
      href: '/shop/riding-gear/boots',
      src: 'https://cdn.mxlocker.com/categories/boots_new_1920x2560.webp',
      alt: 'Boots',
      label: 'Boots',
    },
    {
      href: '/shop/riding-gear/gear',
      src: 'https://cdn.mxlocker.com/categories/gear_new_1920x2560.webp',
      alt: 'Gear',
      label: 'Gear',
    },
    {
      href: '/shop/riding-gear/protective',
      src: 'https://cdn.mxlocker.com/categories/protective_new_1920x2560.webp',
      alt: 'Protective',
      label: 'Protective',
    },
    {
      href: '/shop/riding-gear/helmets',
      src: 'https://cdn.mxlocker.com/categories/helmets_1920x2560.webp',
      alt: 'Helmets',
      label: 'Helmets',
    },
  ];

  const container = useRef<HTMLDivElement>(null);
  const title = 'Popular Categories';

  return (
    <div className="flex flex-col gap-[1.6rem] p-4" ref={ref}>
      <div className="flex w-full justify-between">
        <h4 className="text-[2.4rem] font-semibold">{title}</h4>
        <div className="hidden gap-[1.6rem] sm:flex">
          {/* <Arrow
            variant="left"
            onClick={() => {
              if (container.current) {
                // scroll all the way left
                container.current.scrollLeft = 0;
              }
            }}
          /> */}
          <ChevronLeftIcon
            className="h-14 w-14 cursor-pointer text-brand-secondary"
            onClick={() => {
              if (container.current) {
                // scroll all the way left
                container.current.scrollLeft = 0;
              }
            }}
          />

          {/* <Arrow
            variant="right"
            onClick={() => {
              if (container.current) {
                // scroll all the way right
                container.current.scrollLeft = container.current.scrollWidth;
              }
            }}
          /> */}

          <ChevronRightIcon
            className="h-14 w-14 cursor-pointer text-brand-secondary"
            onClick={() => {
              if (container.current) {
                // scroll all the way left
                container.current.scrollLeft = container.current.scrollWidth;
              }
            }}
          />
        </div>
      </div>

      <div
        className="scroll-smooth-no-safari flex gap-[3rem] overflow-auto pb-4 sm:mx-0 sm:w-full sm:overflow-hidden sm:pb-0"
        ref={container}
      >
        {categories.map((category) => (
          <CategoryLink
            key={category.label}
            src={category.src}
            href={category.href}
            alt={category.alt}
            label={category.label}
            onClick={() => {
              logEvent('select_item', {
                items: [
                  {
                    item_id: category.label,
                    item_name: category.label,
                    item_category: 'popular_riding_gear_categories',
                  },
                ],
              });
            }}
          />
        ))}
      </div>
    </div>
  );
};

interface CategoryLinkProps {
  src: string;
  href: string;
  alt: string;
  label: string;
  onClick?: () => void;
}

const CategoryLink = ({
  src,
  href,
  alt,
  label,
  onClick,
}: CategoryLinkProps) => {
  return (
    <Link href={href} onClick={onClick}>
      <div className="group relative h-[32rem] w-[20rem] overflow-clip rounded-brand sm:h-[46rem] sm:w-[30rem]  ">
        <SafeImage
          src={src}
          alt={alt}
          key={src}
          height={2400}
          width={2400}
          classes="mx-auto inset-0 z-10 group-hover:scale-105 transition-all duration-300 ease-in-out"
        />

        <span className="hover  absolute bottom-[6rem] left-[1rem] z-20 mt-4 text-[2.4rem] font-semibold transition-all  group-hover:text-brand-secondary">
          {label}
        </span>
      </div>
    </Link>
  );
};

export default PopularRidingGearCategories;
